import { ClinicianInfoDto } from '@cerebral-inc/elastic-search';
import { AppointmentType } from 'components/Scheduling';
import { CLINICIAN_MATCHING_BASE_PATH, PROFILE_PHOTO_BASE_PATH } from 'constants/sharedConstants';
import { useQuery } from 'react-query';
import { API_PATHS } from 'types/api-path';
import { ClinicianRoleDisplay } from 'types/clinician';
import { useApi } from 'utils/api';

const ONE_HOUR = 1000 * 60 * 60; // 1 hour
export interface ClinicianInfo {
  airtableName: string;
  id: number;
  role: string;
  autoCashAssignableStates?: string[];
  isCalendarAvailable: boolean;
}

export interface ClinicianInfoResponse {
  fullName: string;
  id: number;
  role: AppointmentType;
  profilePhotoUrl: string;
  autoCashAssignableStates?: string[];
  isCalendarAvailable: boolean;
  clinicianRoleDisplay?: ClinicianRoleDisplay;
  licensedStates?: string[] | null;
}

export const useClinicianInfo = (clinicianId?: number | string) => {
  const api = useApi();

  return useQuery<ClinicianInfoResponse>({
    queryKey: ['clinicianInfo', clinicianId],
    queryFn: () =>
      api
        .get<ClinicianInfoDto>(`${API_PATHS.CLINICIAN_INFO}/${clinicianId}`, { baseURL: CLINICIAN_MATCHING_BASE_PATH })
        .then(response => ({
          fullName: response.data.airtableName,
          id: typeof response.data.id === 'bigint' ? Number(response.data.id) : response.data.id,
          role: (response.data.role === 'prescriber' ? 'provider' : response.data.role) as AppointmentType,
          profilePhotoUrl: `${PROFILE_PHOTO_BASE_PATH}/${clinicianId}`,
          autoCashAssignableStates: response.data.autoCashAssignableStates ?? [],
          isCalendarAvailable: response.data.isCalendarAvailable,
          clinicianRoleDisplay: response.data.clinicianRoleDisplay as ClinicianRoleDisplay,
          licensedStates: response.data.licensedStates
        })),
    enabled: !!clinicianId,
    staleTime: ONE_HOUR,
    meta: {
      errorMessage: 'Failed to get clinician info',
      tags: ['clinician-info', 'fetch'],
      params: {
        clinicianId
      }
    }
  });
};
